if (!customElements.get('watch-strap-configurator')) {
	class WatchStrapConfigurator extends HTMLElement {
		constructor() {
			super()

			this.swiperContainer = this.querySelector('.strap-swiper')
			this.styleTabs = this.querySelectorAll('.watch-style-tab')
			this.faceTabs = this.querySelectorAll('.watch-face-tab')
			this.strapSlides = this.querySelectorAll('.strap-slide')

			// Purchase form elements
			this.watchOnlyForm = this.querySelector('.watch-only-form')
			this.strapOnlyForm = this.querySelector('.strap-only-form')
			this.bundleForm = this.querySelector('.bundle-form')

			// Calculate initial slide based on total slides
			this.numberOfSlides = this.querySelectorAll('.swiper-slide').length
			this.initialSlide = this.numberOfSlides > 5 ? 2 : 0

			// State management
			this.state = {
				currentStyle: '',
				currentFace: '',
				currentWatchId: '',
				currentWatchVariantId: '',
				currentWatchPrice: '',
				currentWatchCurrency: '',
				currentStrapId: '',
				currentStrapVariantId: '',
				currentStrapPrice: '',
				currentStrapCurrency: ''
			}

			// Swiper instance
			this.swiper = null
			this.swiperInitialized = false

			// Track visible slides mapping
			this.visibleSlidesMap = new Map()

			// Default swiper options
			this.swiperOptions = {
				a11y: false,
				initialSlide: this.initialSlide,
				threshold: 3,
				spaceBetween: 9,
				slidesPerView: 5,
				slidesPerGroup: 1,
				resistanceRatio: 0.72,
				centeredSlides: true,
				loop: false,
				navigation: {
					enabled: true,
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev',
				},
				pagination: {
					el: '.swiper-pagination',
					clickable: true,
					bulletClass: 'swiper-pagination-bullet',
					bulletActiveClass: 'swiper-pagination-bullet-active'
				},
				breakpoints: {
					320: {
						navigation: {
							enabled: false
						},
						slidesPerView: 1.8,
						slidesPerGroup: 1,
						initialSlide: 0
					},
					768: {
						enabled: true,
						slidesPerView: 3,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					},
					1200: {
						enabled: true,
						slidesPerView: 5,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					}
				},
				on: {
					slideChange: () => {
						this.updateActiveStrapInfo()
						this.updatePagination()
					},
					init: () => {
						setTimeout(() => {
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.updatePagination()
						}, 100)
					}
				}
			}
		}

		// Add method to calculate initial slide based on visible slides
		calculateInitialSlide(visibleSlidesCount) {
			// For desktop (1200+), center the first slide if more than 5 visible
			// For smaller screens, start at 0 (handled by breakpoints)
			return visibleSlidesCount > 5 ? 2 : 0
		}

		// Add method to update swiper options with new initialSlide
		updateSwiperOptions() {
			const currentBreakpoint = this.swiper?.params?.breakpoints?.[1200] || {}
			const visibleSlidesCount = this.getVisibleStraps().length

			this.initialSlide = this.calculateInitialSlide(visibleSlidesCount)

			// Update the base initialSlide
			this.swiperOptions.initialSlide = this.initialSlide

			// Update breakpoint initialSlide for desktop
			this.swiperOptions.breakpoints[1200].initialSlide = this.initialSlide

			// Update current swiper if it exists
			if (this.swiper && this.swiperInitialized) {
				this.swiper.params.initialSlide = this.initialSlide
				if (window.innerWidth >= 1200) {
					this.swiper.params.initialSlide = this.initialSlide
				}
				this.swiper.update()
			}
		}

		connectedCallback() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.init())
				Shopify.designMode && document.addEventListener('shopify:section:load', () => this.init())
			} else {
				this.init()
			}
		}

		init() {
			this.setupEventListeners()
			this.initializeFirstStyle()
		}

		updatePagination() {
			if (!this.swiper) return

			const slidesPerGroup = Math.floor(this.swiper.params.slidesPerView) || 5
			const currentSlide = this.swiper.realIndex
			const currentGroup = Math.floor(currentSlide / slidesPerGroup)

			const bullets = this.querySelectorAll('.swiper-pagination-bullet')

			bullets.forEach((bullet, index) => {
				const bulletGroup = Math.floor(index / slidesPerGroup)

				if (index % slidesPerGroup === 0) {
					bullet.style.display = 'inline-block'
				} else {
					bullet.style.display = 'none'
				}

				bullet.classList.toggle('swiper-pagination-bullet-active', bulletGroup === currentGroup && index % slidesPerGroup === 0)
			})

			this.swiper.pagination.el.classList.add('pagination-initialized')
		}

		setupEventListeners() {
			this.styleTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleStyleChange(event))
			})

			this.faceTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleFaceChange(event))
			})

			window.addEventListener('resize', () => {
				if (this.swiperInitialized) {
					// Recalculate initial slide on resize
					this.updateSwiperOptions()
					this.swiper.update()
					setTimeout(() => {
						this.updateVisibleSlidesMap()
						this.updateActiveStrapInfo()
						this.updatePagination()
					}, 100)
				}
			})

			if (Shopify.designMode) {
				this.parent = this.closest('section')
				this.observer = new MutationObserver(() => {
					if (this.swiper) {
						this.updateSwiperOptions()
						this.swiper.update()
						setTimeout(() => {
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.updatePagination()
						}, 100)
					}
				})
				this.observer.observe(this.parent, { childList: true, subtree: true })
			}
		}

		initializeFirstStyle() {
			if (this.styleTabs.length > 0) {
				this.styleTabs[0].click()
			} else {
				this.updateVisibleStraps()
			}
		}

		handleStyleChange(event) {
			const style = event.currentTarget.dataset.style
			this.state.currentStyle = style

			this.styleTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')

			this.destroySwiper()

			let firstVisibleFaceTab = null
			let hasActiveFaceTab = false

			this.faceTabs.forEach(faceTab => {
				if (faceTab.dataset.style === style) {
					faceTab.style.display = 'block'
					if (!firstVisibleFaceTab) firstVisibleFaceTab = faceTab
					if (faceTab.classList.contains('active')) hasActiveFaceTab = true
				} else {
					faceTab.style.display = 'none'
				}
			})

			if (!hasActiveFaceTab && firstVisibleFaceTab) {
				firstVisibleFaceTab.classList.add('active')
				this.handleFaceSelection(firstVisibleFaceTab)
			} else {
				const currentActiveFaceTab = this.querySelector('.watch-face-tab.active')
				if (currentActiveFaceTab && currentActiveFaceTab.dataset.style === style) {
					this.handleFaceSelection(currentActiveFaceTab)
				} else if (firstVisibleFaceTab) {
					firstVisibleFaceTab.classList.add('active')
					this.handleFaceSelection(firstVisibleFaceTab)
				}
			}

			// Update initialSlide before updating visible straps
			this.updateSwiperOptions()
			this.updateVisibleStraps()
		}

		handleFaceChange(event) {
			this.destroySwiper()

			this.faceTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')
			this.handleFaceSelection(event.currentTarget)

			// Update initialSlide after face change
			setTimeout(() => {
				this.updateSwiperOptions()
				this.updateVisibleStraps()
			}, 50)
		}

		destroySwiper() {
			if (this.swiper && this.swiperInitialized) {
				this.swiper.pagination.el.classList.remove('pagination-initialized')

				this.swiper.destroy(true, true)
				this.swiper = null
				this.swiperInitialized = false
				this.visibleSlidesMap.clear()

				const bullets = this.querySelectorAll('.swiper-pagination-bullet')
				bullets.forEach(bullet => {
					bullet.style.display = ''
					bullet.classList.remove('swiper-pagination-bullet-active')
					bullet.removeAttribute('aria-label')
					bullet.removeAttribute('aria-controls')
				})
			}
		}

		handleFaceSelection(faceTab) {
			this.state.currentFace = faceTab.dataset.face
			this.state.currentWatchId = faceTab.dataset.watchId
			this.getWatchData(this.state.currentFace)
			this.activateWatchFaces(this.state.currentFace)
			this.updatePurchaseForms()
		}

		getWatchData(face) {
			const watchFace = this.querySelector(`.watch-face[data-face="${face}"]`)
			if (watchFace) {
				this.state.currentWatchVariantId = watchFace.dataset.watchVariant
				this.state.currentWatchPrice = watchFace.dataset.watchPrice
				this.state.currentWatchCurrency = watchFace.dataset.watchCurrency
			}
		}

		activateWatchFaces(face) {
			this.querySelectorAll('.watch-face').forEach(watchFace => {
				watchFace.classList.toggle('active', watchFace.dataset.face === face)
			})
		}

		updateVisibleStraps() {
			this.getVisibleStraps()

			requestAnimationFrame(() => {
				setTimeout(() => {
					this.initSwiper()
				}, 100)
			})
		}

		getVisibleStraps() {
			const visibleStraps = []
			this.visibleSlidesMap.clear()

			this.strapSlides.forEach(strap => {
				if (strap.dataset.style === this.state.currentStyle && strap.style.display !== 'none') {
					strap.style.display = 'block'
					visibleStraps.push(strap)
				} else {
					strap.style.display = 'none'
				}
			})

			return visibleStraps
		}

		updateVisibleSlidesMap() {
			if (!this.swiper) return

			this.visibleSlidesMap.clear()
			const visibleElements = this.querySelectorAll('.strap-slide[style*="block"]')

			visibleElements.forEach((element, swiperIndex) => {
				this.visibleSlidesMap.set(swiperIndex, element)
			})
		}

		initSwiper() {
			if (this.swiperInitialized) {
				this.swiper.destroy(true, true)
				this.swiperInitialized = false
			}

			const visibleSlides = this.getVisibleStraps()
			if (visibleSlides.length === 0) return

			// Use the updated initialSlide from updateSwiperOptions()
			// The swiperOptions are already updated with the correct initialSlide
			this.swiper = new Swiper(this.swiperContainer, this.swiperOptions)

			this.swiper.wrapperEl.setAttribute('aria-live', 'polite')
			this.swiper.wrapperEl.id = `watch-swiper-${this.dataset.sectionId || Date.now()}`

			// Update slides accessibility after swiper init
			setTimeout(() => {
				this.swiper.slides.forEach((slide, index) => {
					slide.setAttribute('role', 'group')
					slide.setAttribute('aria-label', `${index + 1} / ${this.swiper.slides.length}`)
					slide.dataset.index = index
				})

				this.updateVisibleSlidesMap()

				// Navigation button accessibility
				const navButtons = [
					this.swiper.navigation.prevEl,
					this.swiper.navigation.nextEl
				]

				navButtons.forEach((button, index) => {
					if (button) {
						const type = index === 0 ? 'Previous' : 'Next'
						button.setAttribute('aria-label', `${type} strap`)
						button.setAttribute('aria-controls', this.swiper.wrapperEl.id)
					}
				})

				// Pagination setup
				this.setupPaginationAccessibility()

				this.swiperInitialized = true

				// Final update after everything is set up
				setTimeout(() => {
					this.updateActiveStrapInfo()
					this.updatePagination()
				}, 150)
			}, 50)
		}

		setupPaginationAccessibility() {
			if (!this.swiper) return

			const slidesPerGroup = Math.floor(this.swiper.params.slidesPerView) || 5
			const paginationBullets = this.querySelectorAll('.swiper-pagination-bullet')
			let groupCounter = 1

			paginationBullets.forEach((bullet, index) => {
				if (index % slidesPerGroup === 0) {
					bullet.setAttribute('aria-label', `Go to slide group ${groupCounter}`)
					bullet.setAttribute('aria-controls', this.swiper.wrapperEl.id)
					bullet.style.display = 'inline-block'
					groupCounter++
				} else {
					bullet.style.display = 'none'
					bullet.removeAttribute('aria-label')
					bullet.removeAttribute('aria-controls')
				}
			})
		}

		updateActiveStrapInfo() {
			if (!this.swiper || typeof this.swiper.realIndex === 'undefined') {
				// Fallback: use first visible slide if swiper not ready
				const firstVisibleSlide = this.querySelector('.strap-slide[style*="block"]')
				if (firstVisibleSlide) {
					this.updateStrapInfo(firstVisibleSlide)
				}
				return
			}

			// Use the mapping to get the correct visible slide element
			const swiperIndex = this.swiper.realIndex
			const activeSlideElement = this.visibleSlidesMap.get(swiperIndex)

			if (activeSlideElement) {
				this.updateStrapInfo(activeSlideElement)
			} else {
				// Fallback: find by swiper slide index
				const swiperSlide = this.swiper.slides[swiperIndex]
				if (swiperSlide) {
					const strapSlide = swiperSlide.querySelector('.strap-slide')
					if (strapSlide && strapSlide.style.display !== 'none') {
						this.updateStrapInfo(strapSlide)
					}
				}
			}
		}

		updateStrapInfo(strapElement) {
			const strapInfo = this.querySelector('.strap-info')
			if (!strapInfo || !strapElement) return

			const strapName = strapElement.dataset.productTitle || ''
			const strapPrice = parseFloat(strapElement.dataset.price) || 0

			this.state.currentStrapVariantId = strapElement.dataset.variantId || ''
			this.state.currentStrapId = strapElement.dataset.productId || ''
			this.state.currentStrapCurrency = strapElement.dataset.currency || ''

			const nameElement = strapInfo.querySelector('.strap-name')
			const priceElement = this.querySelector('.strap-price')

			if (nameElement) nameElement.textContent = strapName
			if (priceElement) {
				const symbol = this.getCurrencySymbol(this.state.currentStrapCurrency)
				priceElement.textContent = `${symbol}${parseFloat(strapPrice)}`
			}

			this.updatePurchaseForms()
		}

		getCurrencySymbol(currencyCode) {
			const currencies = {
				'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$',
				'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥', 'INR': '₹', 'HKD': 'HK$',
				'RUB': '₽', 'BRL': 'R$', 'SEK': 'kr', 'NZD': 'NZ$', 'MXN': 'MX$',
				'SGD': 'S$', 'KRW': '₩', 'NOK': 'kr', 'TRY': '₺', 'ZAR': 'R'
			}
			return currencies[currencyCode] || currencyCode + ' '
		}

		updatePurchaseForms() {
			this.updateForm(this.watchOnlyForm, 'id', this.state.currentWatchVariantId, !this.state.currentWatchVariantId)
			this.updateForm(this.strapOnlyForm, 'id', this.state.currentStrapVariantId, !this.state.currentStrapVariantId)

			if (this.bundleForm) {
				const watchInput = this.bundleForm.querySelector('input[name="items[0][id]"]')
				const strapInput = this.bundleForm.querySelector('input[name="items[1][id]"]')
				const button = this.bundleForm.querySelector('button')

				if (watchInput) watchInput.value = this.state.currentWatchVariantId
				if (strapInput) strapInput.value = this.state.currentStrapVariantId
				if (button) button.disabled = !(this.state.currentWatchVariantId && this.state.currentStrapVariantId)
			}
		}

		updateForm(form, inputName, value, disabled) {
			if (!form) return

			const input = form.querySelector(`input[name="${inputName}"]`)
			const button = form.querySelector('button')

			if (input) input.value = value || ''
			if (button) button.disabled = disabled
		}

		disconnectedCallback() {
			this.destroySwiper()
			if (this.observer) {
				this.observer.disconnect()
			}
		}
	}

	customElements.define('watch-strap-configurator', WatchStrapConfigurator)
}
